import { useQuery } from '@tanstack/react-query';
import { CalendarClock, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { Seccion } from '@/shared/ui/Seccion';
import { BarrasAgrupadas, enDinero, enPorcentaje } from '@/shared/ui/graficos';

interface Mes {
  periodo: string;
  cobros: number;
  total: number;
}

interface Comparativo {
  desde: string;
  hasta: string;
  desdeAntes: string;
  hastaAntes: string;
  total: number;
  totalAntes: number;

  /**
   * Puede NO VENIR. El backend la manda en `null` cuando el periodo anterior fue cero, y
   * Jackson esta configurado con `default-property-inclusion: non_null`: un nulo no se
   * serializa, desaparece del JSON. Comparar contra `null` aqui no sirve de nada.
   */
  variacion?: number | null;
  meses: Mes[];
  mesesAntes: Mes[];
}

const SERIES = [
  { clave: 'ahora', nombre: 'Este período', color: '#0E7C5A' },
  { clave: 'antes', nombre: 'El anterior', color: '#9CA3AF' },
] as const;

function enFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return dia && mes && anio ? `${dia}/${mes}/${anio}` : iso;
}

export function PanelComparativo({ desde, hasta }: { desde: string; hasta: string }) {
  const panel = useQuery({
    queryKey: ['informe-comparativo', desde, hasta],
    queryFn: () =>
      api.get<Comparativo>('/informes/recaudacion/comparativo', { query: { desde, hasta } }),
  });

  if (panel.isLoading) {
    return <Cargando titulo="Cargando" detalle="comparando con el período anterior" />;
  }

  const datos = panel.data;
  if (!datos) {
    return null;
  }

  // Se normaliza UNA vez: a partir de aqui, «no hay variacion» es siempre `null`.
  const variacion = datos.variacion ?? null;
  const subio = (variacion ?? 0) >= 0;
  const Flecha = variacion === null ? Minus : subio ? TrendingUp : TrendingDown;

  const cuantos = Math.max(datos.meses.length, datos.mesesAntes.length);
  const emparejados = Array.from({ length: cuantos }, (_, i) => ({
    etiqueta: datos.meses[i]?.periodo ?? `Mes ${i + 1}`,
    ahora: datos.meses[i]?.total ?? 0,
    antes: datos.mesesAntes[i]?.total ?? 0,
  }));

  return (
    <div className="space-y-4">
      <div className="grid gap-3 lg:grid-cols-3">
        <Tarjeta
          rotulo="Este período"
          detalle={`${enFecha(datos.desde)} a ${enFecha(datos.hasta)}`}
          valor={enDinero(datos.total)}
          tono="text-marca-700"
          fondo="bg-marca-50"
        />
        <Tarjeta
          rotulo="El anterior"
          detalle={`${enFecha(datos.desdeAntes)} a ${enFecha(datos.hastaAntes)}`}
          valor={enDinero(datos.totalAntes)}
          tono="text-armazon-600"
          fondo="bg-armazon-50"
        />
        <div
          className={`rounded-2xl border border-white/60 p-4 ${
            variacion === null ? 'bg-armazon-50' : subio ? 'bg-marca-50' : 'bg-red-50'
          }`}
        >
          <span className="mb-2 flex items-center gap-2">
            <Flecha
              className={`size-4 ${
                variacion === null ? 'text-armazon-500' : subio ? 'text-marca-700' : 'text-red-700'
              }`}
              aria-hidden
            />
            <span className="text-[10px] font-bold uppercase tracking-wider text-armazon-500">
              Variación
            </span>
          </span>
          <span
            className={`block text-xl font-bold tabular-nums ${
              variacion === null ? 'text-armazon-500' : subio ? 'text-marca-700' : 'text-red-700'
            }`}
          >
            {variacion === null ? '—' : `${subio ? '+' : ''}${enPorcentaje(variacion)}`}
          </span>
          <span className="mt-1 block text-[11px] text-armazon-400">
            {variacion === null
              ? 'En el período anterior no entró nada'
              : 'Respecto al período anterior'}
          </span>
        </div>
      </div>

      <Seccion
        titulo="Mes a mes"
        ayuda="Emparejados por posición: el primer mes de este período contra el primero del anterior"
      >
        <BarrasAgrupadas datos={emparejados} claveEtiqueta="etiqueta" series={SERIES} />
      </Seccion>

      <Seccion titulo="Detalle" ayuda="Lo que entró en cada mes de los dos períodos">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-armazon-50/80">
              <tr>
                {['Mes', 'Este período', 'El anterior', 'Diferencia'].map((c, i) => (
                  <th
                    key={c}
                    scope="col"
                    className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-armazon-500 ${
                      i === 0 ? 'text-left' : 'text-right'
                    }`}
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-armazon-100">
              {emparejados.map((e) => {
                const diferencia = e.ahora - e.antes;

                return (
                  <tr key={e.etiqueta} className="transition-colors hover:bg-armazon-50/60">
                    <td className="px-3 py-2.5 text-xs font-medium text-armazon-700">
                      {e.etiqueta}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs tabular-nums">
                      {enDinero(e.ahora)}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs tabular-nums text-armazon-500">
                      {enDinero(e.antes)}
                    </td>
                    <td
                      className={`px-3 py-2.5 text-right text-xs font-semibold tabular-nums ${
                        diferencia >= 0 ? 'text-marca-700' : 'text-red-700'
                      }`}
                    >
                      {diferencia >= 0 ? '+' : '−'}
                      {enDinero(Math.abs(diferencia))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Seccion>
    </div>
  );
}

function Tarjeta({
  rotulo,
  detalle,
  valor,
  tono,
  fondo,
}: {
  rotulo: string;
  detalle: string;
  valor: string;
  tono: string;
  fondo: string;
}) {
  return (
    <div className={`rounded-2xl border border-white/60 p-4 ${fondo}`}>
      <span className="mb-2 flex items-center gap-2">
        <CalendarClock className={`size-4 ${tono}`} aria-hidden />
        <span className="text-[10px] font-bold uppercase tracking-wider text-armazon-500">
          {rotulo}
        </span>
      </span>
      <span className={`block truncate text-xl font-bold tabular-nums ${tono}`}>{valor}</span>
      <span className="mt-1 block text-[11px] text-armazon-400">{detalle}</span>
    </div>
  );
}
